/**
 * 404 · Page Not Found
 *
 * Replaces the old `<Route path="*" element={<Home />} />` catch-all that
 * made EVERY unknown URL return the homepage HTML with a 200 OK. Google saw
 * dozens of unrelated URLs serving identical content and flagged the site
 * with "Duplicate without user-selected canonical" + "Duplicate, Google
 * chose different canonical than user" in Search Console.
 *
 * Now: unknown URLs render this page which
 *   1. Applies `useNoIndex()` — Google's second-pass (JS-rendered) crawl
 *      picks up `<meta name="robots" content="noindex,nofollow">`.
 *   2. Presents a real "Page Not Found" screen with a route back to the
 *      catalog so real visitors don't hit a dead end.
 *
 * Note: because this is a React SPA on a static host, the HTTP status is
 * still 200 (the platform can't return a real 404 for an unknown path). The
 * noindex meta is what tells Google the URL is not a canonical page. This
 * is the standard SPA workaround Google explicitly documents in Search
 * Central: "For soft 404s, add a noindex meta tag to the rendered page."
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import useNoIndex from "../hooks/useNoIndex";
import SEO from "../components/SEO";

export default function NotFound() {
  useNoIndex();

  return (
    <>
      <SEO
        title="Page Not Found · ProSource Labs"
        description="The page you're looking for could not be found. Return to the ProSource Labs catalog."
        path="/404"
        noindex
      />
      <main
        data-testid="not-found-page"
        className="min-h-[70vh] flex items-center justify-center px-6 py-24"
      >
        <div className="max-w-xl w-full text-center">
          <div className="overline text-[#00C3FF]">Error 404</div>
          <h1 className="font-heading text-5xl sm:text-6xl font-light leading-[1.05] tracking-tight mt-6">
            Page not found.
          </h1>
          <p className="text-base text-[#8A94A6] mt-6 leading-relaxed">
            The URL you followed either no longer exists or was mistyped.
            Nothing to see here — but the catalog is a click away.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
            <Link
              to="/"
              data-testid="not-found-home-link"
              className="btn-primary"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Return Home
            </Link>
            <Link
              to="/shop"
              data-testid="not-found-shop-link"
              className="btn-ghost"
            >
              <Search className="w-4 h-4" aria-hidden="true" />
              Browse Catalog
            </Link>
          </div>
        </div>
      </main>
    </>
  );
}
